import { useCallback, type FC } from "react";
import { useStore } from "react-redux";
import { Controller, useWatch, type Control } from "react-hook-form";

import { useRootSelector } from "@ds/hooks";

import {
  type FormationChangeHandlerDecorator,
  type FormationChangeHandlerInputData,
} from "@ds/modules/settings/projects/components/forms/controls/types";
import { CampaignsAutocompleteRaw } from "@ds/modules/content/campaigns/components/campaigns-autocomplete";

import { type RootState } from "@ds/store/bootstrap";

import { useDetailsInlineSaving } from "@ds/components/details-view/hooks";
import { LabelWrapper } from "@ds/components/forms/labels";
import { isFormationPropertyRequired } from "@ds/utils/validation";

import { selectCampaignById } from "../../../redux/selectors";

export const CampaignFormationControl: FC<{
  control: Control<Experience | Project>;
  name: `formation.properties.${string}` | `template.experience_formation.properties.${string}`;
  property: TemplateCampaignFormationProperty;
  autoFocus?: boolean;
  excludeLabel?: boolean;
  onBeforeChangeHandlerDecorator: FormationChangeHandlerDecorator;
  onBlur?: () => void;
}> = ({ control, name, property, autoFocus, excludeLabel, onBeforeChangeHandlerDecorator, onBlur }) => {
  const onBlurHandler = useDetailsInlineSaving(onBlur);
  const propertyValue = (useWatch({ name, control }) as EntityCampaignFormationProperty)?.value;
  const currentCampaignName = useRootSelector(
    state => selectCampaignById(state, Number(propertyValue) || 0)?.name ?? "",
  );
  const store = useStore<RootState>();
  const onChangeHandler = useCallback(
    (onChange: (inputData: FormationChangeHandlerInputData) => void) =>
      onBeforeChangeHandlerDecorator(
        onChange,
        currentCampaignName,
        inputData => selectCampaignById(store.getState(), Number(inputData) || 0)?.name ?? null,
      ),
    [currentCampaignName, onBeforeChangeHandlerDecorator, store],
  );

  return (
    <Controller
      name={`${name}.value`}
      control={control}
      render={({ field: { ref, value, onChange, onBlur: onBlurFn, ...restFields }, fieldState }) => {
        const element = (
          <CampaignsAutocompleteRaw
            inputRef={ref}
            value={Number(value)}
            autoFocus={autoFocus}
            onChange={onChangeHandler(onChange)}
            onBlur={onBlurHandler(onBlurFn)}
            {...restFields}
          />
        );

        if (excludeLabel) {
          return element;
        }

        return (
          <LabelWrapper
            label={property.title}
            className="col-8"
            isRequired={isFormationPropertyRequired(property.metadata?.validation_rules)}
            error={fieldState.error}
          >
            {element}
          </LabelWrapper>
        );
      }}
    />
  );
};
